import React, { useMemo } from 'react'
import { Autocomplete, Avatar, Box, TextField, Typography } from '@mui/material'
import { User, ApproverSettings } from 'types'

interface Props {
  users: User[]
  label: string
  type: keyof ApproverSettings
  selectedIds: string[]
  onAddApprover: (type: keyof ApproverSettings, value: string[]) => void
}

const RenderSingleApproverSetting: React.FC<Props> = ({ users, label, type, selectedIds, onAddApprover }) => {
  // Memoised because Autocomplete clears what you have typed whenever `value`
  // is a new array. The settings menu that hosts this modal re-renders every
  // second for its session timer, so an inline map wiped the input within a
  // second of typing.
  const value = useMemo(
    () => selectedIds.map(id => users.find(u => u.user_uuid === id)).filter(Boolean),
    [selectedIds, users]
  )

  return (
    <Box>
      <Typography textAlign={'left'} fontWeight={600} gutterBottom>
        {label}
      </Typography>
      <Autocomplete
        multiple
        options={users}
        value={value}
        getOptionLabel={option => option?.name || ''}
        onChange={(_, newValue) => {
          const newIds = newValue.map(user => user.user_uuid)
          onAddApprover(type, newIds)
        }}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => {
            const { key: _key, onDelete, ...rest } = getTagProps({ index })
            return (
              <Box
                key={option.user_uuid || index}
                {...rest}
                sx={{ display: 'flex', alignItems: 'center', height: '100%' }}
              >
                <Avatar
                  alt={option.name || option.firstName}
                  src={option.image_512}
                  sx={{ height: 36, width: 36, mr: 0.5 }}
                  onClick={onDelete} // 👈 Enable click-to-remove
                  style={{ cursor: 'pointer' }}
                />
              </Box>
            )
          })
        }
        renderInput={params => <TextField {...params} label={`Add ${label} Approvers`} variant="outlined" />}
      />
    </Box>
  )
}

export default RenderSingleApproverSetting
